import { Link } from 'react-router';
import { ClipboardCheck, CheckCircle, XCircle } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import type { SupervisorAssessment } from '@/types';
import {
  ASSESSMENT_TYPE_BADGES,
  ASSESSMENT_TYPE_LABELS,
  ASSESSMENT_STATUS_BADGES,
  ASSESSMENT_STATUS_LABELS,
} from '../_labels';

export function AssessmentsSection({ assessments }: { assessments?: SupervisorAssessment[] }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Mis Evaluaciones</h2>
      </div>
      {!assessments?.length ? (
        <EmptyState
          title="Sin evaluaciones"
          description="No tienes evaluaciones programadas o completadas."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {assessments.map((assessment) => (
            <Link
              key={assessment.id}
              to={`/competency/assessments/${assessment.id}`}
              className="block rounded-xl border border-border bg-card p-5 transition-colors hover:bg-accent/50"
            >
              <div className="flex flex-wrap gap-1.5">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    ASSESSMENT_TYPE_BADGES[assessment.assessment_type],
                  )}
                >
                  {ASSESSMENT_TYPE_LABELS[assessment.assessment_type] ?? assessment.assessment_type}
                </span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs font-medium',
                    ASSESSMENT_STATUS_BADGES[assessment.status],
                  )}
                >
                  {ASSESSMENT_STATUS_LABELS[assessment.status]}
                </span>
              </div>

              {assessment.status === 'completed' && assessment.score != null && (
                <div className="mt-3 flex items-center gap-3">
                  <span className="text-2xl font-bold text-card-foreground">
                    {assessment.score}%
                  </span>
                  {assessment.passed ? (
                    <span className="flex items-center gap-1 text-sm font-medium text-green-600">
                      <CheckCircle className="h-4 w-4" /> Aprobado
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm font-medium text-red-600">
                      <XCircle className="h-4 w-4" /> Reprobado
                    </span>
                  )}
                </div>
              )}

              <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
                {assessment.assessor && (
                  <p>
                    Evaluador: {assessment.assessor.first_name} {assessment.assessor.last_name}
                  </p>
                )}
                {assessment.created_at && <p>Creada: {formatDate(assessment.created_at)}</p>}
                {assessment.completed_at && (
                  <p>Completada: {formatDate(assessment.completed_at)}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
